import type {Fetcher} from './fetcher.ts';
/**
 * Decorator for a Fetcher that caches responses in memory.
 *
 * Only GET and HEAD requests are cached.
 */
export class CachingFetcher implements Fetcher {
  fetcher: Fetcher;
  cache: Map<RequestInfo, Promise<Response>>;

  constructor(fetcher: Fetcher) {
    this.fetcher = fetcher;
    this.cache = new Map();
  }

  async fetch(requestOrUrl: RequestInfo, options: RequestInit = undefined): Promise<Response> {
    // We're conservative in that we only cache GET and HEAD requests.
    // See https://developer.mozilla.org/en-US/docs/Glossary/cacheable
    const method = (options?.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      return this.fetcher.fetch(requestOrUrl, options);
    }

    const cached = this.cache.get(requestOrUrl);
    if (cached !== undefined) {
      const response = await cached;
      // We need to clone since reading the response payload consumes it.
      return response.clone();
    }

    const responsePromise = this.fetcher.fetch(requestOrUrl, options);

    // Cache immediately so that when multiple requests are fired in quick
    // succession, we don't hit the network multiple times.
    this.cache.set(requestOrUrl, responsePromise);

    try {
      const response = await responsePromise;
      // Evict the response from the cache if there isn't a success status.  304
      // means it was cached at a lower level.
      if (!(response.status >= 200 && response.status <= 299) &&
        response.status !== 304) {
        // Not cacheable.
        this.cache.delete(requestOrUrl);
      }

      // We need to clone since reading the response payload consumes it.
      return response.clone();
    } catch (error) {
      // If there's an error, evict it from the cache.
      this.cache.delete(requestOrUrl);
      throw error;
    }
  }
}
